// #region Imports
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import KeyboardTabIcon from "@mui/icons-material/KeyboardTab";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { useDialogSurface } from "_shared/_views/dialogs/dialog";
import { ReactNode, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  StaffingPickerLayout,
  staffingSidePaneRequest,
  STAFFING_PANE_SHUT,
} from "work/staffing/_views/staffing-picker-stage";
// #endregion

/** What the control that puts the picker away is called, to a reader and to a test. */
export const CLOSE_STAFFING_PANEL = "Close the work picker";

/**
 * What that same control is called where there is no profile left on the screen beside it.
 *
 * At the narrow stage the picker has the dialog to itself, so putting it away is not "close the
 * panel" -- there is nothing behind it to go on reading. It is going back to the person.
 */
export const BACK_TO_THE_PROFILE = "Back to the profile";

/** What the strip the picker shrinks to is called, once the reader has expanded the profile. */
export const SHOW_STAFFING_PANEL = "Show the work picker";

// #region Styled Components
/**
 * The picker's column of the dialog.
 *
 * **It declares no width of its own.** Its width is the grid track the dialog gives it, which is
 * the whole of the arrangement: a pane that sized itself would be free to size itself wrong, and
 * the first version of this surface did exactly that -- `min(860px, 100%)` against a box with no
 * width came out one pixel wide, mounted and visible and holding its table, in every browser.
 * Nothing here resolves against anything, so there is nothing left to resolve against nothing.
 *
 * `minWidth: 0` because a grid item's floor is its content, and the table inside is wider than the
 * track at the narrow stage; without this the table would push the dialog wider than the window and
 * the dialog would get the horizontal scrollbar the table is supposed to keep to itself.
 */
const PickerPane = styled("div", {
  shouldForwardProp: (prop) => prop !== "open",
})<{ open: boolean }>(({ theme, open }) => ({
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  gridColumn: 2,
  gridRow: 2,
  height: "100%",
  minHeight: 0,
  minWidth: 0,
  // The track narrows to nothing when the picker is put away, and this is what clips the pane's
  // contents while it does.
  overflow: "hidden",
  transition: theme.transitions.create("visibility", {
    duration: theme.transitions.duration.leavingScreen,
  }),
  // A clipped pane is invisible but still something a screen reader can be walked into. `visibility`
  // is what says it is gone; transitioning it is what holds it on the screen for the length of the
  // animation that takes it away.
  visibility: open ? "visible" : "hidden",
  width: "100%",
}));

/** Where the profile's context rail goes: the first column, in place of the profile itself. */
const RailSlot = styled("div")(() => ({
  boxSizing: "border-box",
  gridColumn: 1,
  gridRow: 2,
  height: "100%",
  minWidth: 0,
  overflow: "hidden",
  width: "100%",
}));

/**
 * The picker's own contents: as wide as the pane, which is as wide as the dialog allows.
 *
 * Nothing heavy narrows with the pane on the way out, because the table is unmounted before the
 * animation starts -- what is left to narrow is a title bar and a close button.
 */
const PanelContainer = styled("section")(() => ({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  minHeight: 0,
  minWidth: 0,
  width: "100%",
}));

/**
 * The title bar and the table together, which is the picker proper.
 *
 * `hidden` here is the HTML attribute doing its own job: not drawn, out of the tab order, out of
 * the accessibility tree -- and still mounted, with everything the reader had typed into it.
 */
const PanelBody = styled("div")(() => ({
  display: "flex",
  flex: 1,
  flexDirection: "column",
  minHeight: 0,
  minWidth: 0,
  "&[hidden]": { display: "none" },
}));

const PanelTitleBar = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "row",
  gap: theme.spacing(1),
  justifyContent: "space-between",
  padding: theme.spacing(2, 2, 1),
}));

const PanelTitle = styled(Typography)(() => ({
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

/**
 * Where the table goes: everything the title bar leaves, and no scrollbar of its own.
 *
 * The table inside scrolls its own rows under a header that stays put. If this scrolled as well the
 * reader would have two scrollbars for one list.
 */
const PanelContent = styled("div")(({ theme }) => ({
  display: "flex",
  flex: 1,
  flexDirection: "column",
  minHeight: 0,
  minWidth: 0,
  overflow: "hidden",
  padding: theme.spacing(0, 2, 2),
}));

/** The picker shrunk to the one control that brings it back, with the profile expanded over it. */
const CollapsedPicker = styled("div")(({ theme }) => ({
  alignItems: "center",
  borderLeft: `1px solid ${theme.palette.divider}`,
  display: "flex",
  flexDirection: "column",
  height: "100%",
  paddingTop: theme.spacing(2),
  width: "100%",
}));
// #endregion

export type StaffingDestinationPanelProps = {
  /** Whether the picker is out. */
  open: boolean;
  /** What the picker is called, which is also its accessible name. */
  title: string;
  /** How the dialog is divided between the profile and the picker at this width. */
  layout: StaffingPickerLayout;
  /** The profile reduced to who it is, for the widths that have room for a rail and no more. */
  rail: ReactNode;
  /** Put the picker away, leaving the profile behind it as it was. */
  onClose: () => void;
  /** Bring the table back after the reader expanded the profile over it. */
  onShowPicker: () => void;
  /** The table the picker carries. */
  children: ReactNode;
};

/**
 * The work picker, as a column of the dialog it is opened in.
 *
 * **Not a second dialog, not a nested one, and not a sheet over the profile.** The point of the
 * surface is that the reader can see whose profile they are staffing while they choose the work,
 * and a dialog -- stacked or nested -- takes the profile away to do it. A sheet floated over the
 * right of the same dialog does not take it away, but it does cover it, which is the same loss
 * with extra animation. So the dialog is divided instead: the profile has one column and the
 * picker has the other, and there is no width at which either is drawn over the other.
 *
 * What changes with the width is how much of the profile there is room for, and nothing else. Wide
 * enough for both and both are there; not wide enough and the profile is a context rail saying who
 * this is; narrower still and it is pushed off the screen by the table, with the way back on the
 * picker's own title bar. See `staffing-picker-stage` for the widths and where they come from.
 *
 * It mounts into the dialog's paper rather than where it is written, for two reasons. The paper is
 * the grid the columns belong to, and it does not scroll -- the content box inside it does -- so a
 * pane placed there stays put while the reader scrolls the profile beside it. And the paper is
 * inside the dialog's focus trap, which anywhere else on the page is not: a pane mounted outside the
 * open dialog could not be reached by keyboard at all. Where there is no dialog to mount into it
 * draws where it stands, which is what a test that renders the picker on its own sees.
 */
export default function StaffingDestinationPanel(
  props: Readonly<StaffingDestinationPanelProps>,
) {
  const open = props.open;
  const layout = props.layout;
  const onClose = props.onClose;
  const showingTable = open && layout.picker === "table";
  const surface = useDialogSurface();
  const requestSidePane = surface?.requestSidePane;
  const [panelRegion, setPanelRegion] = useState<HTMLElement | null>(null);
  const openerRef = useRef<HTMLElement | null>(null);

  /**
   * Focus follows the picker out and back again.
   *
   * Out, because a reader who opened the picker and has not moved their hands is still standing on
   * the profile: Escape would reach the dialog rather than the picker and take the whole profile
   * away, and a screen reader would be told nothing had happened. The picker takes the focus and
   * announces itself.
   *
   * Back, because a picker that has been put away is `inert`, and the browser answers that by
   * dropping focus to the document body -- inside a dialog, that is a reader with nothing under
   * their hands at all. The control they opened it from is where they were.
   */
  useEffect(() => {
    if (open) {
      const opener = document.activeElement;
      if (!openerRef.current) openerRef.current = opener instanceof HTMLElement ? opener : null;
      // Also when the picker shrinks to a strip and when it comes back, because the control the
      // reader pressed to do either is in the half that has just stopped being drawn -- and a
      // browser answers that by dropping focus on the document body, which inside a dialog is a
      // reader with nothing under their hands at all.
      panelRegion?.focus();
      return;
    }

    const opener = openerRef.current;
    openerRef.current = null;
    if (opener?.isConnected) opener.focus();
  }, [open, panelRegion, showingTable]);

  /**
   * Escape closes the picker from wherever the reader's hands happen to be.
   *
   * The handler on the pane below only hears an Escape that starts inside the pane, which takes it
   * on trust that the focus move above landed and stayed there. In a real browser it need not have:
   * the dialog's own focus trap and the table's first render both move focus after the pane has
   * asked for it, and then Escape starts somewhere else and the pane never hears it. The jsdom test
   * dispatches the key AT the pane, so it cannot tell the two apart -- which is why this was green
   * in unit tests and dead in the browser.
   *
   * Listening on the document while open makes the key mean the same thing wherever it starts.
   * Capture, and stopped there, because the dialog underneath would otherwise hear it first and
   * take the whole profile away -- which is the thing the pane handler exists to prevent.
   */
  useEffect(() => {
    if (!open) return undefined;

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      onClose();
    };

    document.addEventListener("keydown", closeOnEscape, true);
    return () => document.removeEventListener("keydown", closeOnEscape, true);
  }, [onClose, open]);

  useEffect(() => {
    if (!requestSidePane) return;
    requestSidePane(open ? staffingSidePaneRequest(layout) : STAFFING_PANE_SHUT);
    return () => requestSidePane(null);
  }, [layout, open, requestSidePane]);

  const pane = (
    <>
      {open && layout.profile === "rail" && <RailSlot>{props.rail}</RailSlot>}
      <PickerPane
        open={open}
        // Escape is the reader saying "put this away", and the dialog underneath would otherwise
        // hear it first and close the whole profile.
        onKeyDown={(event) => {
          if (event.key !== "Escape" || !open) return;
          event.stopPropagation();
          onClose();
        }}
      >
        {/* The pane keeps its place in the dialog's grid at no width, so a picker that has been put
            away would otherwise leave its own close button in the tab order behind the profile.
            `inert` says so from the moment it starts closing, where `visibility` waits for the
            animation to finish. */}
        <PanelContainer aria-label={props.title} inert={!open} ref={setPanelRegion} tabIndex={-1}>
          {/* Drawn not at all rather than unmounted while the reader has the profile expanded over
              it. The table is what they were working in -- their term, their page, their sort -- and
              a rail that threw it away would make expanding the profile cost the search. Closing the
              picker is the thing that takes it down, and that is the reader saying they are done. */}
          <PanelBody hidden={!showingTable}>
            <PanelTitleBar>
              <PanelTitle variant="h5">{props.title}</PanelTitle>
              <Tooltip title={layout.profile === "hidden" ? BACK_TO_THE_PROFILE : "Close panel"}>
                <span>
                  <IconButton
                    aria-label={
                      layout.profile === "hidden" ? BACK_TO_THE_PROFILE : CLOSE_STAFFING_PANEL
                    }
                    size="medium"
                    onClick={onClose}
                  >
                    {layout.profile === "hidden" ? (
                      <ArrowBackIcon fontSize="large" />
                    ) : (
                      <KeyboardTabIcon fontSize="large" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            </PanelTitleBar>
            <PanelContent>{props.children}</PanelContent>
          </PanelBody>
          {open && layout.picker === "rail" && (
            <CollapsedPicker>
              <Tooltip title={SHOW_STAFFING_PANEL} placement="left">
                <span>
                  <IconButton
                    aria-label={SHOW_STAFFING_PANEL}
                    size="small"
                    onClick={props.onShowPicker}
                  >
                    <ChevronLeftIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </CollapsedPicker>
          )}
        </PanelContainer>
      </PickerPane>
    </>
  );

  const container = surface?.sidePanelContainer;
  return container ? createPortal(pane, container) : pane;
}
